import { Fragment } from 'react';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  DefaultEntityFilters,
  EntityAutocompletePicker,
  EntityFilter,
  EntityListProvider,
  EntityNamespacePicker,
  EntityLifecyclePicker,
  EntityOwnerPicker,
  EntityTagPicker,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { OverflowTooltip, TableColumn } from '@backstage/core-components';
import { JsonObject } from '@backstage/types';
import { Entity } from '@backstage/catalog-model';
import { Box, makeStyles } from '@material-ui/core';
import { CustomEntityKindPicker } from './CustomEntityKindPicker';

/** Filter on metadata.labels['product-line'] for Group entities. */
class ProductLineFilter implements EntityFilter {
  constructor(readonly values: string[]) {}
  filterEntity(entity: Entity): boolean {
    if (!this.values.length) return true;
    const line = entity.metadata.labels?.['product-line'];
    return typeof line === 'string' && this.values.includes(line);
  }
}

/** Filter on metadata.labels['category'] for Resource entities. */
class CategoryFilter implements EntityFilter {
  constructor(readonly values: string[]) {}
  filterEntity(entity: Entity): boolean {
    if (!this.values.length) return true;
    const cat = entity.metadata.labels?.['category'];
    return typeof cat === 'string' && this.values.includes(cat);
  }
}

type ExtendedFilters = DefaultEntityFilters & {
  productLine?: ProductLineFilter;
  category?: CategoryFilter;
};

const useStyles = makeStyles(theme => ({
  // ── Horizontal filter bar ────────────────────────────────────────────────
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  filterItem: {
    width: 250,
    paddingRight: 10,
    paddingTop: 2, // room for the pinned label above
    '& .MuiFormControl-root': {
      width: '100%',
      marginTop: 0,
      marginBottom: 0,
    },
    '& .MuiAutocomplete-root': {
      width: '100%',
    },
    '& .MuiInputBase-root': {
      height: 36,
      borderRadius: 8,
      fontSize: '0.82rem',
      backgroundColor: 'rgba(255,255,255,0.05)',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.2)',
      borderRadius: 8,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255,255,255,0.45)',
    },
    '& .MuiInputLabel-outlined': {
      fontSize: '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.04em',
      transform: 'translate(0, -20px) scale(1)',
      transformOrigin: 'top left',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(0, -20px) scale(1)',
    },
    '& .MuiOutlinedInput-notchedOutline legend': {
      maxWidth: 0,
    },
    '& .MuiChip-root': {
      height: 18,
      fontSize: '0.7rem',
    },
  },
  pageWrapper: {
    padding: 24,
  },
}));

const createUserEmailColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'User Email',
  field: 'entity.spec.profile.email',
  render: row => {
    const profile = row.entity.spec?.profile;
    const userEmail = (profile as JsonObject)?.['email'];
    const emailText = typeof userEmail === 'string' ? userEmail : 'N/A';
    return <OverflowTooltip text={emailText} placement="bottom-start" />;
  },
});

const createUserDisplayNameColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Display Name',
  field: 'entity.metadata.name',
  render: row => {
    const profile = row.entity.metadata?.name;
    const displayText = typeof profile === 'string' ? profile : 'N/A';
    return <OverflowTooltip text={displayText} placement="bottom-start" />;
  },
});

const createUserMemberOfColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Member Of',
  field: 'entity.spec.memberOf',
  render: row => {
    const memberOf = row.entity.spec?.memberOf;
    if (Array.isArray(memberOf) && memberOf.length > 0) {
      return (
        <span>
          {memberOf.map((group, index) => {
            const groupName = String(group);
            return (
              <Fragment key={groupName}>
                <a
                  href={`/catalog/default/group/${groupName}`}
                  style={{ textDecoration: 'none' }}
                  target="_blank"
                >
                  {groupName}
                </a>
                {index < memberOf.length - 1 && ', '}
              </Fragment>
            );
          })}
        </span>
      );
    } else if (typeof memberOf === 'string') {
      return (
        <a
          href={`/catalog/default/group/${memberOf}`}
          style={{ textDecoration: 'none', color: '#1976d2' }}
        >
          {memberOf}
        </a>
      );
    }
    return <OverflowTooltip text="N/A" placement="bottom-start" />;
  },
});

const createGroupMemberCountColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Members',
  field: 'entity.metadata.name',
  sorting: false,
  render: row => {
    const memberRelations =
      row.entity.relations?.filter(r => r.type === 'hasMember') ?? [];
    return (
      <OverflowTooltip
        text={memberRelations.length.toString()}
        placement="bottom-start"
      />
    );
  },
});

const catalogColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  if (entityListContext.filters.kind?.value === 'user') {
    return [
      CatalogTable.columns.createNameColumn(),
      createUserDisplayNameColumn(),
      createUserEmailColumn(),
      createUserMemberOfColumn(),
    ];
  }
  if (entityListContext.filters.kind?.value === 'group') {
    return [
      CatalogTable.columns.createNameColumn(),
      createGroupMemberCountColumn(),
      CatalogTable.columns.createNamespaceColumn(),
    ];
  }
  if (entityListContext.filters.kind?.value === 'resource') {
    return [
      CatalogTable.columns.createNameColumn(),
      CatalogTable.columns.createSystemColumn(),
      CatalogTable.columns.createOwnerColumn(),
      CatalogTable.columns.createLabelColumn('category'),
      CatalogTable.columns.createSpecLifecycleColumn(),
    ];
  }
  return [
    CatalogTable.columns.createNameColumn(),
    CatalogTable.columns.createOwnerColumn(),
    CatalogTable.columns.createSpecTypeColumn(),
    CatalogTable.columns.createSpecLifecycleColumn(),
  ];
};

function FilterBar() {
  const classes = useStyles();
  const { filters } = useEntityList<ExtendedFilters>();
  const kind = filters.kind?.value;

  if (kind === 'user' || kind === 'location') return null;

  if (kind === 'group') {
    return (
      <Box className={classes.filterBar}>
        <Box className={classes.filterItem}>
          <EntityAutocompletePicker<ExtendedFilters, 'productLine'>
            label="Product Line"
            name="productLine"
            path="metadata.labels.product-line"
            Filter={ProductLineFilter}
          />
        </Box>
        <Box className={classes.filterItem}><EntityOwnerPicker /></Box>
        <Box className={classes.filterItem}><EntityLifecyclePicker /></Box>
        <Box className={classes.filterItem}><EntityTagPicker /></Box>
      </Box>
    );
  }

  if (kind === 'resource') {
    return (
      <Box className={classes.filterBar}>
        <Box className={classes.filterItem}><EntityOwnerPicker /></Box>
        <Box className={classes.filterItem}><EntityLifecyclePicker /></Box>
        <Box className={classes.filterItem}>
          <EntityAutocompletePicker<ExtendedFilters, 'category'>
            label="Category"
            name="category"
            path="metadata.labels.category"
            Filter={CategoryFilter}
          />
        </Box>
        <Box className={classes.filterItem}><EntityNamespacePicker /></Box>
      </Box>
    );
  }

  return (
    <Box className={classes.filterBar}>
      <Box className={classes.filterItem}><EntityOwnerPicker /></Box>
      <Box className={classes.filterItem}><EntityLifecyclePicker /></Box>
      <Box className={classes.filterItem}><EntityTagPicker /></Box>
      <Box className={classes.filterItem}><EntityNamespacePicker /></Box>
    </Box>
  );
}

export function CustomCatalogPage() {
  const classes = useStyles();
  return (
    <EntityListProvider>
      <Box className={classes.pageWrapper}>
        <CustomEntityKindPicker />
        {/* ── Horizontal filter bar above table ── */}
        <FilterBar />
        <CatalogTable
          columns={catalogColumnsFunc}
          tableOptions={{ pageSize: 20, pageSizeOptions: [20, 50, 100] }}
        />
        {/* ── Sidebar layout: filters right of table (see shiny-new-buttons) ──
        <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <CatalogTable
              columns={catalogColumnsFunc}
              tableOptions={{ pageSize: 50, pageSizeOptions: [20, 50, 100] }}
            />
          </Box>
          <FilterBar />
        </Box>
        */}
      </Box>
    </EntityListProvider>
  );
}
